import React from "react";
import ReusableDropdown, { unifiedControlStyle } from "../../../components/ReusableDropdown";

// Search / department / status / company filter controls
const EmployeeFilters = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  searchTerm,
  setSearchTerm,
  searchClientCode,
  setSearchClientCode,
  codeSearchField,
  setCodeSearchField,
  filterDepartment,
  setFilterDepartment,
  departments = [],
  filterStatusSearch,
  setFilterStatusSearch,
  setFilterStatus,
  filterCompanySearch,
  setFilterCompanySearch,
  setFilterCompany,
  companies,
}) => {
  // Every filter cell is a column with its control pinned to the bottom, and
  // every label row shares one minimum height — otherwise the identifier
  // box's taller label (it carries a segmented toggle) pushes its input below
  // the line the other inputs sit on.
  const groupStyle = { ...styles.filterGroup, display: "flex", flexDirection: "column", minWidth: 0 };
  const labelStyle = { ...styles.filterLabel, display: "flex", alignItems: "center", minHeight: "28px" };
  const controlStyle = { marginTop: "auto" };

  return (
  <div style={styles.filterContainer}>
    {/* Search Input */}
    <div style={groupStyle}>
      <label style={labelStyle}>
        {text[language].searchEmployee}
      </label>
      <input
        type="text"
        placeholder={text[language].searchNameIdEmail}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ ...styles.filterInput, ...controlStyle }}
      />
    </div>

    {/* Identifier box — searches ONE of Client Code / National ID. Both at
        once is useless: a short client code is a substring of plenty of
        14-digit national IDs, which buries the row you actually want. */}
    <div style={groupStyle}>
      <label
        style={{
          ...labelStyle,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <span>{text[language].searchBy}</span>
        <span style={{ display: "flex", gap: "4px" }}>
          {[
            { key: "clientCode", label: text[language].clientCode },
            { key: "nationalId", label: text[language].nationalId },
          ].map((opt) => {
            const active = codeSearchField === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setCodeSearchField(opt.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: "26px",
                  padding: "0 10px",
                  fontSize: "12px",
                  fontWeight: active ? 700 : 500,
                  borderRadius: "6px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  border: `1px solid ${active ? "#3b82f6" : theme === "dark" ? "rgba(255,255,255,0.18)" : "#d1d5db"}`,
                  background: active ? "#3b82f6" : theme === "dark" ? "rgba(255,255,255,0.06)" : "#ffffff",
                  color: active ? "#ffffff" : theme === "dark" ? "rgba(255,255,255,0.75)" : "#374151",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </span>
      </label>
      <input
        type="text"
        placeholder={
          codeSearchField === "nationalId"
            ? text[language].searchNationalId
            : text[language].searchClientCode
        }
        value={searchClientCode}
        onChange={(e) => setSearchClientCode(e.target.value)}
        style={{ ...styles.filterInput, ...controlStyle }}
      />
    </div>

    {/* Department Filter */}
    <div style={groupStyle}>
      <label style={labelStyle}>
        {text[language].department}
      </label>

      {/* Free text still works (the page filters by substring), but the arrow
          lists every department present on the loaded employees. */}
      <div style={{ ...controlStyle, position: 'relative' }}>
        <ReusableDropdown
          items={[
            { key: '', label: text[language].allDepartments || text[language].department },
            ...departments.map((dep) => ({ key: dep, label: dep })),
          ]}
          value={filterDepartment}
          onChange={(val) => setFilterDepartment(val)}
          onSelect={(item) => setFilterDepartment(item.key)}
          onClear={() => setFilterDepartment('')}
          placeholder={text[language].searchDepartment}
          getItemKey={(it) => it.key}
          getItemLabel={(it) => it.label}
          theme={theme}
          language={language}
          inputStyle={unifiedControlStyle(theme, language === 'ar')}
          maxItems={8}
          showAllOnArrowClick={true}
        />
      </div>
    </div>

    {/* Status Filter */}
    <div style={groupStyle}>
      <label style={labelStyle}>{text[language].Status}</label>
      <div style={{ ...controlStyle, position: 'relative' }}>
        <ReusableDropdown
          items={[
            { key: '', label: text[language].allStatuses },
            { key: 'Active', label: text[language].statusActive },
            { key: 'OnLeave', label: text[language].statusOnLeave },
            { key: 'Inactive', label: text[language].statusInactive },
          ]}
          value={filterStatusSearch}
          onChange={(val) => setFilterStatusSearch(val)}
          onSelect={(item) => {
            setFilterStatus(item.key);
            setFilterStatusSearch(item.label);
          }}
          placeholder={text[language].selectStatus || text[language].Status}
          getItemKey={(it) => it.key}
          getItemLabel={(it) => it.label}
          theme={theme}
          language={language}
          inputStyle={unifiedControlStyle(theme, language === 'ar')}
          maxItems={6}
        />
      </div>
    </div>
    <div style={groupStyle}>
      <label style={labelStyle}>{text[language].company}</label>
      <div style={{ ...controlStyle, position: 'relative' }}>
        <ReusableDropdown
          items={[{ key: '', label: text[language].allCompanies }, ...(companies || []).map(c => ({ key: c, label: c }))]}
          value={filterCompanySearch}
          onChange={(val) => setFilterCompanySearch(val)}
          onSelect={(item) => {
            setFilterCompany(item.key || '');
            setFilterCompanySearch(item.label || '');
          }}
          placeholder={text[language].allCompanies}
          getItemKey={(it) => it.key}
          getItemLabel={(it) => it.label}
          theme={theme}
          language={language}
          inputStyle={unifiedControlStyle(theme, language === 'ar')}
          maxItems={8}
        />
      </div>
    </div>
  </div>
  );
};

export default EmployeeFilters;
